'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Backend: replace with real monthly appointment + revenue data from API
const appointmentData = [
  { month: 'Jan', appointments: 0, revenue: 0 },
  { month: 'Feb', appointments: 0, revenue: 0 },
  { month: 'Mar', appointments: 0, revenue: 0 },
  { month: 'Apr', appointments: 0, revenue: 0 },
  { month: 'May', appointments: 0, revenue: 0 },
  { month: 'Jun', appointments: 0, revenue: 0 },
  { month: 'Jul', appointments: 0, revenue: 0 },
  { month: 'Aug', appointments: 0, revenue: 0 },
];

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-600 text-foreground mb-1">{label} 2026</p>
      {payload.map((p) => (
        <div key={`tt-${p.name}`} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground capitalize">{p.name}:</span>
          <span className="font-600 text-foreground tabular-nums">
            {p.name === 'revenue' ? `₱${p.value.toLocaleString()}` : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function RevenueChart() {
  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-700 text-foreground">Appointment Volume & Revenue</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Monthly trend — 2026</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-primary" />
            <span className="text-muted-foreground">Appointments</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-success" />
            <span className="text-muted-foreground">Revenue (₱)</span>
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={appointmentData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradAppt" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.25} />
              <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--success)" stopOpacity={0.25} />
              <stop offset="95%" stopColor="var(--success)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} width={30} />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="appointments" name="appointments" stroke="var(--primary)" strokeWidth={2} fill="url(#gradAppt)" />
          <Area type="monotone" dataKey="revenue" name="revenue" stroke="var(--success)" strokeWidth={2} fill="url(#gradRevenue)" />
        </AreaChart>
      </ResponsiveContainer>
      <div className="mt-3 flex items-center justify-center text-xs text-muted-foreground">
        No appointment data yet — data will populate as jobs are created and completed
      </div>
    </div>
  );
}